import React, { useContext, useEffect, useRef } from "react"
import { Platform, StyleSheet, View } from "react-native"
import FilledButtonM from "@common/components/button/FilledButtonM"
import { WIDE_BREAKPOINT } from "@common/components/funnel/FunnelQuestionScreen"
import { ModalSkeleton } from "@common/modals/ModalSkeleton"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"

const SHEET_MAX_WIDTH = 520
const CARD_WIDTH = 440

/**
 * Un volet du funnel d'inscription : posé en bas sur un téléphone, carte
 * centrée sur grand écran, avec son titre, sa croix et son bouton de
 * fermeture. Le clavier y entre à l'ouverture et Échap le referme.
 *
 * Le contenu n'est monté qu'à l'ouverture : un volet qui garde un état
 * (l'onglet de « Une heure de {prénom} ») repart de zéro à chaque fois.
 * `closeLabel={null}` retire le bouton du bas, pour un volet qui porte sa
 * propre action (le stage : « Recomposer »).
 */
const InscriptionSheet = ({
    isVisible,
    onClose,
    title,
    closeLabel = "Compris",
    children,
}) => {
    const width = useContext(DimensionsContext)?.width ?? 0

    return (
        <ModalSkeleton
            isVisible={isVisible}
            onClose={onClose}
            title={title}
            style={
                width >= WIDE_BREAKPOINT
                    ? styles.card
                    : {
                          ...styles.sheet,
                          width: Math.min(width, SHEET_MAX_WIDTH),
                      }
            }
        >
            <SheetBody title={title} closeLabel={closeLabel} onClose={onClose}>
                {children}
            </SheetBody>
        </ModalSkeleton>
    )
}

const SheetBody = ({ title, closeLabel, onClose, children }) => {
    const dialog = useRef(null)

    useEffect(() => {
        // Un champ du volet qui a déjà pris le focus (autoFocus) le garde : le
        // volet ne le reprend que s'il est resté dehors.
        const active =
            typeof document !== "undefined" ? document.activeElement : null
        if (active && dialog.current?.contains?.(active)) return
        dialog.current?.focus?.()
    }, [])
    useEffect(() => {
        if (Platform.OS !== "web" || typeof document === "undefined") {
            return undefined
        }
        const onKeyDown = (event) => {
            if (event.key === "Escape") onClose?.()
        }
        document.addEventListener("keydown", onKeyDown)
        return () => document.removeEventListener("keydown", onKeyDown)
    }, [onClose])

    return (
        <View
            ref={dialog}
            role="dialog"
            aria-modal
            aria-label={title}
            tabIndex={-1}
            style={styles.dialog}
        >
            {children}
            {closeLabel ? (
                <FilledButtonM
                    mode="outlined"
                    label={closeLabel}
                    color={theme.colors.primary}
                    onPress={onClose}
                    style={styles.close}
                />
            ) : null}
        </View>
    )
}

const styles = StyleSheet.create({
    // La modale centre sa carte : la marge haute la pousse en bas de l'écran.
    sheet: {
        marginTop: "auto",
        borderRadius: 0,
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
    },
    card: { width: CARD_WIDTH, borderRadius: 16 },
    dialog: { gap: 14 },
    close: { width: "100%", alignSelf: "stretch", marginBottom: 0 },
})

export default InscriptionSheet
